import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Truck, Globe2, Clock, ShieldCheck, Package, Mail } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    badge: 'Shipping & Delivery',
    title: 'Shipping Policy',
    lastUpdated: 'Last Updated',
    intro: 'Penlab Peptide ships research peptides worldwide from six logistics hubs. All shipments are tracked, insured and dispatched in temperature-controlled packaging to preserve peptide integrity.',
    flatRateTitle: 'Flat-rate shipping',
    flatRateBody: (rate, threshold) => `A single flat rate of ${rate} (or equivalent in your selected currency) applies worldwide on every order below ${threshold}. Shipping is free on every order at or above ${threshold}. No hidden surcharges, no per-country differences, no surprises at checkout.`,
    freeShippingTitle: 'Free shipping on orders over',
    freeShippingBody: (p) => `All orders with a subtotal of ${p} or equivalent in your selected currency ship free. The discount is applied automatically at checkout, no code required.`,
    ratesTitle: 'Shipping rates by destination',
    zoneCountriesLabel: 'Countries',
    zoneRateLabel: 'Flat rate',
    zoneFreeLabel: 'Free over',
    handlingTitle: 'Handling & transit time',
    handlingBody: 'Handling time is 1-2 business days from order confirmation. Transit time is typically 3-7 business days depending on the destination zone. You will receive a tracking number by email the moment your parcel leaves our warehouse.',
    packagingTitle: 'Packaging & preservation',
    packagingBody: 'Every shipment is packed in insulated boxes with cold-gel packs where required. Outer packaging is discreet and unbranded. All peptides are supplied FOR RESEARCH USE ONLY, Not for human consumption.',
    trackingTitle: 'Tracking & carriers',
    trackingBody: 'We work with DHL Express, UPS, FedEx, Royal Mail, Correos and regional partners. Your tracking number is clickable in the shipped-confirmation email and routes to the correct carrier portal automatically.',
    customsTitle: 'Customs & duties',
    customsBody: 'International orders may be subject to import duties or taxes levied by your destination country. These are the buyer\'s responsibility. If a parcel is refused at customs, return shipping fees will be deducted from any refund issued.',
    contact: 'Questions about a shipment?',
    contactText: 'Our logistics team is available in English, Spanish and Russian and replies within one business day.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (flat rate only)',
  },
  es: {
    badge: 'Envíos y Entrega',
    title: 'Política de Envíos',
    lastUpdated: 'Última actualización',
    intro: 'Penlab Peptide envía péptidos de investigación en todo el mundo desde seis centros logísticos. Todos los envíos son rastreados, asegurados y despachados en embalajes con control de temperatura para preservar la integridad del péptido.',
    flatRateTitle: 'Tarifa plana de envío',
    flatRateBody: (rate, threshold) => `Se aplica una tarifa plana única de ${rate} (o equivalente en tu moneda) a cualquier pedido en todo el mundo inferior a ${threshold}. El envío es gratis para cualquier pedido igual o superior a ${threshold}. Sin cargos ocultos, sin diferencias por país, sin sorpresas al finalizar la compra.`,
    freeShippingTitle: 'Envío gratis para pedidos superiores a',
    freeShippingBody: (p) => `Todos los pedidos con un subtotal de ${p} o equivalente en tu moneda tienen envío gratis. El descuento se aplica automáticamente al finalizar la compra, no se necesita código.`,
    ratesTitle: 'Tarifas de envío por destino',
    zoneCountriesLabel: 'Países',
    zoneRateLabel: 'Tarifa plana',
    zoneFreeLabel: 'Gratis desde',
    handlingTitle: 'Preparación y tiempos de tránsito',
    handlingBody: 'El tiempo de preparación es de 1 a 2 días laborables desde la confirmación del pedido. El tiempo de tránsito suele ser de 3 a 7 días laborables según la zona de destino. Recibirás un número de seguimiento por correo en el momento en que salga del almacén.',
    packagingTitle: 'Embalaje y preservación',
    packagingBody: 'Cada envío se embala en cajas aisladas con acumuladores de frío cuando es necesario. El embalaje exterior es discreto y sin marca. Todos los péptidos se suministran SOLO PARA USO EN INVESTIGACIÓN, No apto para consumo humano.',
    trackingTitle: 'Seguimiento y mensajería',
    trackingBody: 'Trabajamos con DHL Express, UPS, FedEx, Royal Mail, Correos y socios regionales. Tu número de seguimiento está activo en el correo de confirmación de envío y dirige automáticamente al portal del transportista correcto.',
    customsTitle: 'Aduanas e impuestos',
    customsBody: 'Los pedidos internacionales pueden estar sujetos a aranceles o impuestos de importación que imponga tu país de destino. Son responsabilidad del comprador. Si un paquete es rechazado en aduana, los gastos de devolución se deducirán del reembolso.',
    contact: '¿Preguntas sobre un envío?',
    contactText: 'Nuestro equipo de logística atiende en inglés, español y ruso y responde en un día laborable.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (tarifa plana únicamente)',
  },
  ru: {
    badge: 'Доставка',
    title: 'Политика доставки',
    lastUpdated: 'Последнее обновление',
    intro: 'Penlab Peptide доставляет исследовательские пептиды по всему миру с шести логистических хабов. Все отправления отслеживаются, застрахованы и отправляются в упаковке с контролем температуры для сохранения целостности пептидов.',
    flatRateTitle: 'Единая ставка доставки',
    flatRateBody: (rate, threshold) => `Единая фиксированная ставка ${rate} (или эквивалент в вашей валюте) применяется по всему миру к любому заказу на сумму менее ${threshold}. Доставка бесплатна для любого заказа на сумму от ${threshold}. Никаких скрытых доплат, никаких отличий по странам, никаких сюрпризов при оформлении.`,
    freeShippingTitle: 'Бесплатная доставка для заказов от',
    freeShippingBody: (p) => `Все заказы на сумму ${p} или эквивалент в выбранной валюте доставляются бесплатно. Скидка применяется автоматически при оформлении, промокод не нужен.`,
    ratesTitle: 'Тарифы по направлениям',
    zoneCountriesLabel: 'Страны',
    zoneRateLabel: 'Фиксированная ставка',
    zoneFreeLabel: 'Бесплатно от',
    handlingTitle: 'Обработка и сроки доставки',
    handlingBody: 'Обработка занимает 1-2 рабочих дня с момента подтверждения заказа. Транспортировка обычно длится 3-7 рабочих дней в зависимости от зоны. Трек-номер придёт на почту сразу после отправки.',
    packagingTitle: 'Упаковка и сохранность',
    packagingBody: 'Каждая посылка упакована в изотермические коробки, при необходимости, с хладагентами. Внешняя упаковка нейтральная, без брендирования. Все пептиды поставляются ТОЛЬКО ДЛЯ ИССЛЕДОВАТЕЛЬСКИХ ЦЕЛЕЙ, не для потребления человеком.',
    trackingTitle: 'Отслеживание и перевозчики',
    trackingBody: 'Мы работаем с DHL Express, UPS, FedEx, Royal Mail, Correos и региональными партнёрами. Номер отслеживания кликабелен в письме об отправке и автоматически ведёт на портал нужного перевозчика.',
    customsTitle: 'Таможня и пошлины',
    customsBody: 'Международные заказы могут облагаться ввозными пошлинами или налогами, установленными страной получателя. Их оплачивает покупатель. Если посылка не принята на таможне, расходы на обратную доставку вычитаются из возмещения.',
    contact: 'Вопросы по доставке?',
    contactText: 'Наша логистическая команда работает на английском, испанском и русском языках и отвечает в течение одного рабочего дня.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (только фиксированная ставка)',
  },
    de: {
    badge: 'Versand & Lieferung',
    title: 'Versandrichtlinie',
    lastUpdated: 'Zuletzt aktualisiert',
    intro: 'Penlab Peptide versendet Forschungspeptide weltweit von sechs Logistikzentren aus. Alle Sendungen werden nachverfolgt, versichert und in temperaturkontrollierter Verpackung verschickt, um die Peptid-Integrität zu erhalten.',
    flatRateTitle: 'Pauschalversand',
    flatRateBody: (rate, threshold) => `Ein einheitlicher Pauschalbetrag von ${rate} (oder dem Gegenwert in der von Ihnen gewählten Währung) gilt weltweit für jede Bestellung unter ${threshold}. Der Versand ist kostenlos für jede Bestellung ab ${threshold}. Keine versteckten Zuschläge, keine länderspezifischen Unterschiede, keine Überraschungen beim Bezahlvorgang.`,
    freeShippingTitle: 'Kostenloser Versand für Bestellungen über',
    freeShippingBody: (p) => `Alle Bestellungen mit einem Zwischensumme-Betrag von ${p} oder dem Gegenwert in der von Ihnen gewählten Währung werden versandkostenfrei verschickt. Der Rabatt wird beim Bezahlvorgang automatisch angewendet, kein Code erforderlich.`,
    ratesTitle: 'Versandkosten nach Zielort',
    zoneCountriesLabel: 'Länder',
    zoneRateLabel: 'Pauschalbetrag',
    zoneFreeLabel: 'Kostenlos über',
    handlingTitle: 'Bearbeitungs- & Transportzeit',
    handlingBody: 'Die Bearbeitungszeit beträgt 1-2 Werktage ab Bestellbestätigung. Die Transportzeit liegt je nach Zielzone typischerweise bei 3-7 Werktagen. Sie erhalten eine Sendungsverfolgungsnummer per E-Mail, sobald Ihr Paket unser Lager verlässt.',
    packagingTitle: 'Verpackung & Konservierung',
    packagingBody: 'Jede Sendung wird in isolierten Boxen mit Kühlgel-Packs, wo erforderlich, verpackt. Die Außenverpackung ist diskret und ohne Branding. Alle Peptide werden AUSSCHLIESSLICH FÜR FORSCHUNGSZWECKE bereitgestellt, Nicht für den menschlichen Verzehr.',
    trackingTitle: 'Sendungsverfolgung & Carrier',
    trackingBody: 'Wir arbeiten mit DHL Express, UPS, FedEx, Royal Mail, Correos und regionalen Partnern zusammen. Ihre Sendungsverfolgungsnummer ist in der Versandbestätigungs-E-Mail anklickbar und leitet automatisch zum richtigen Carrier-Portal weiter.',
    customsTitle: 'Zoll & Abgaben',
    customsBody: 'Internationale Bestellungen können Einfuhrzöllen oder Steuern unterliegen, die von Ihrem Zielland erhoben werden. Diese liegen in der Verantwortung des Käufers. Wenn ein Paket beim Zoll abgelehnt wird, werden die Rücksendekosten von einer eventuell gewährten Rückerstattung abgezogen.',
    contact: 'Fragen zu einer Sendung?',
    contactText: 'Unser Logistikteam steht auf Englisch, Spanisch und Russisch zur Verfügung und antwortet innerhalb eines Werktages.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (nur Pauschalbetrag)',
  },
    fr: {
    badge: 'Expédition et livraison',
    title: 'Politique d\'expédition',
    lastUpdated: 'Dernière mise à jour',
    intro: 'Penlab Peptide expédie des peptides de recherche dans le monde entier depuis six centres logistiques. Tous les envois sont suivis, assurés et expédiés dans un emballage à température contrôlée afin de préserver l\'intégrité des peptides.',
    flatRateTitle: 'Expédition à tarif forfaitaire',
    flatRateBody: (rate, threshold) => `Un tarif forfaitaire unique de ${rate} (ou l\'équivalent dans la devise que vous avez sélectionnée) s\'applique dans le monde entier pour toute commande inférieure à ${threshold}. L\'expédition est gratuite pour toute commande égale ou supérieure à ${threshold}. Aucun supplément caché, aucune différence selon le pays, aucune surprise au moment du paiement.`,
    freeShippingTitle: 'Livraison gratuite pour les commandes supérieures à',
    freeShippingBody: (p) => `Toutes les commandes avec un sous-total de ${p} ou l\'équivalent dans la devise que vous avez sélectionnée sont expédiées gratuitement. La remise est appliquée automatiquement lors du paiement, aucun code requis.`,
    ratesTitle: 'Tarifs d\'expédition par destination',
    zoneCountriesLabel: 'Pays',
    zoneRateLabel: 'Tarif forfaitaire',
    zoneFreeLabel: 'Gratuit au-dessus de',
    handlingTitle: 'Traitement et délai de transit',
    handlingBody: 'Le délai de traitement est de 1 à 2 jours ouvrables à compter de la confirmation de la commande. Le délai de transit est généralement de 3 à 7 jours ouvrables selon la zone de destination. Vous recevrez un numéro de suivi par e-mail dès que votre colis quittera notre entrepôt.',
    packagingTitle: 'Emballage et conservation',
    packagingBody: 'Chaque envoi est emballé dans des boîtes isothermes avec des packs de gel réfrigérant lorsque cela est nécessaire. L\'emballage extérieur est discret et non marqué. Tous les peptides sont fournis POUR LA RECHERCHE UNIQUEMENT, Non destinés à la consommation humaine.',
    trackingTitle: 'Suivi et transporteurs',
    trackingBody: 'Nous travaillons avec DHL Express, UPS, FedEx, Royal Mail, Correos et des partenaires régionaux. Votre numéro de suivi est cliquable dans l\'e-mail de confirmation d\'expédition et redirige automatiquement vers le portail du transporteur approprié.',
    customsTitle: 'Douanes et droits',
    customsBody: 'Les commandes internationales peuvent être soumises à des droits de douane ou taxes imposés par votre pays de destination. Ceux-ci sont à la charge de l\'acheteur. Si un colis est refusé par la douane, les frais de retour seront déduits de tout remboursement émis.',
    contact: 'Des questions concernant un envoi ?',
    contactText: 'Notre équipe logistique est disponible en anglais, espagnol et russe et répond dans un délai d\'un jour ouvrable.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (tarif forfaitaire uniquement)',
  },
    it: {
    badge: 'Spedizione e consegna',
    title: 'Politica di spedizione',
    lastUpdated: 'Ultimo aggiornamento',
    intro: 'Penlab Peptide spedisce peptidi di ricerca in tutto il mondo da sei hub logistici. Tutte le spedizioni sono tracciate, assicurate e inviate in imballaggi a temperatura controllata per preservare l\'integrità dei peptidi.',
    flatRateTitle: 'Spedizione a tariffa fissa',
    flatRateBody: (rate, threshold) => `Una singola tariffa fissa di ${rate} (o equivalente nella valuta selezionata) si applica in tutto il mondo su ogni ordine inferiore a ${threshold}. La spedizione è gratuita su ogni ordine pari o superiore a ${threshold}. Nessun sovrapprezzo nascosto, nessuna differenza per paese, nessuna sorpresa al checkout.`,
    freeShippingTitle: 'Spedizione gratuita per ordini superiori a',
    freeShippingBody: (p) => `Tutti gli ordini con un subtotale di ${p} o equivalente nella valuta selezionata vengono spediti gratuitamente. Lo sconto viene applicato automaticamente al checkout, nessun codice richiesto.`,
    ratesTitle: 'Tariffe di spedizione per destinazione',
    zoneCountriesLabel: 'Paesi',
    zoneRateLabel: 'Tariffa fissa',
    zoneFreeLabel: 'Gratis oltre',
    handlingTitle: 'Gestione e tempi di transito',
    handlingBody: 'Il tempo di gestione è di 1-2 giorni lavorativi dalla conferma dell\'ordine. Il tempo di transito è in genere di 3-7 giorni lavorativi a seconda della zona di destinazione. Riceverai un numero di tracking via email nel momento in cui il tuo pacco lascia il nostro magazzino.',
    packagingTitle: 'Imballaggio e conservazione',
    packagingBody: 'Ogni spedizione è confezionata in scatole isolate con sacchetti di gel refrigerante ove necessario. L\'imballaggio esterno è discreto e non marchiato. Tutti i peptidi sono forniti SOLO PER USO DI RICERCA, Non per consumo umano.',
    trackingTitle: 'Tracking e corrieri',
    trackingBody: 'Collaboriamo con DHL Express, UPS, FedEx, Royal Mail, Correos e partner regionali. Il tuo numero di tracking è cliccabile nell\'email di conferma di avvenuta spedizione e rimanda automaticamente al portale del corriere corretto.',
    customsTitle: 'Dogana e dazi',
    customsBody: 'Gli ordini internazionali possono essere soggetti a dazi di importazione o tasse imposte dal paese di destinazione. Questi sono a carico dell\'acquirente. Se un pacco viene rifiutato alla dogana, le spese di restituzione verranno detratte da qualsiasi rimborso emesso.',
    contact: 'Domande su una spedizione?',
    contactText: 'Il nostro team logistico è disponibile in inglese, spagnolo e russo e risponde entro un giorno lavorativo.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (solo tariffa fissa)',
  },
    pl: {
    badge: 'Wysyłka i dostawa',
    title: 'Polityka wysyłki',
    lastUpdated: 'Ostatnia aktualizacja',
    intro: 'Penlab Peptide wysyła peptydy badawcze na cały świat z sześciu centrów logistycznych. Wszystkie przesyłki są śledzone, ubezpieczone i wysyłane w opakowaniach z kontrolowaną temperaturą w celu zachowania integralności peptydów.',
    flatRateTitle: 'Zryczałtowana wysyłka',
    flatRateBody: (rate, threshold) => `Jednolita zryczałtowana opłata w wysokości ${rate} (lub równowartość w wybranej przez Ciebie walucie) obowiązuje na całym świecie dla każdego zamówienia poniżej ${threshold}. Wysyłka jest bezpłatna dla każdego zamówienia o wartości równej lub wyższej niż ${threshold}. Brak ukrytych dopłat, brak różnic między krajami, żadnych niespodzianek przy finalizacji zamówienia.`,
    freeShippingTitle: 'Darmowa wysyłka dla zamówień powyżej',
    freeShippingBody: (p) => `Wszystkie zamówienia z sumą częściową ${p} lub równowartością w wybranej przez Ciebie walucie są wysyłane bezpłatnie. Zniżka jest naliczana automatycznie przy finalizacji zamówienia, nie jest wymagany żaden kod.`,
    ratesTitle: 'Stawki wysyłki według miejsca docelowego',
    zoneCountriesLabel: 'Kraje',
    zoneRateLabel: 'Stawka zryczałtowana',
    zoneFreeLabel: 'Darmowa powyżej',
    handlingTitle: 'Czas realizacji i transportu',
    handlingBody: 'Czas realizacji zamówienia wynosi 1-2 dni robocze od potwierdzenia zamówienia. Czas transportu wynosi zazwyczaj 3-7 dni roboczych, w zależności od strefy docelowej. W momencie, gdy Twoja przesyłka opuści nasz magazyn, otrzymasz numer śledzenia drogą e-mailową.',
    packagingTitle: 'Pakowanie i zachowanie jakości',
    packagingBody: 'Każda przesyłka jest pakowana w izolowane pudełka z wkładami chłodzącymi tam, gdzie jest to wymagane. Opakowanie zewnętrzne jest dyskretne i nieoznakowane. Wszystkie peptydy są dostarczane WYŁĄCZNIE DO ZASTOSOWAŃ BADAWCZYCH, Nie do spożycia przez ludzi.',
    trackingTitle: 'Śledzenie i przewoźnicy',
    trackingBody: 'Współpracujemy z DHL Express, UPS, FedEx, Royal Mail, Correos oraz regionalnymi partnerami. Twój numer śledzenia jest klikalny w e-mailu z potwierdzeniem wysyłki i automatycznie prowadzi do właściwego portalu przewoźnika.',
    customsTitle: 'Cło i podatki',
    customsBody: 'Zamówienia międzynarodowe mogą podlegać cłom importowym lub podatkom nałożonym przez kraj docelowy. Są one odpowiedzialnością kupującego. Jeśli przesyłka zostanie odrzucona przez urząd celny, koszty zwrotnej wysyłki zostaną potrącone z ewentualnego zwrotu środków.',
    contact: 'Pytania dotyczące przesyłki?',
    contactText: 'Nasz zespół logistyczny jest dostępny w języku angielskim, hiszpańskim i rosyjskim i odpowiada w ciągu jednego dnia roboczego.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (tylko stawka zryczałtowana)',
  },
    nl: {
    badge: 'Verzending & levering',
    title: 'Verzendbeleid',
    lastUpdated: 'Laatst bijgewerkt',
    intro: 'Penlab Peptide verzendt onderzoekspeptiden wereldwijd vanuit zes logistieke hubs. Alle zendingen worden gevolgd, verzekerd en verzonden in temperatuurgecontroleerde verpakkingen om de integriteit van de peptiden te behouden.',
    flatRateTitle: 'Vast tarief voor verzending',
    flatRateBody: (rate, threshold) => `Eén vast tarief van ${rate} (of het equivalent in de door u gekozen valuta) is wereldwijd van toepassing op elke bestelling onder ${threshold}. Verzending is gratis bij elke bestelling van ${threshold} of hoger. Geen verborgen toeslagen, geen verschillen per land, geen verrassingen bij het afrekenen.`,
    freeShippingTitle: 'Gratis verzending bij bestellingen boven',
    freeShippingBody: (p) => `Alle bestellingen met een subtotaal van ${p} of het equivalent in de door u gekozen valuta worden gratis verzonden. De korting wordt automatisch toegepast bij het afrekenen, geen code vereist.`,
    ratesTitle: 'Verzendtarieven per bestemming',
    zoneCountriesLabel: 'Landen',
    zoneRateLabel: 'Vast tarief',
    zoneFreeLabel: 'Gratis boven',
    handlingTitle: 'Verwerking & levertijd',
    handlingBody: 'De verwerkingstijd bedraagt 1-2 werkdagen vanaf de orderbevestiging. De levertijd is doorgaans 3-7 werkdagen, afhankelijk van de bestemmingszone. U ontvangt een trackingnummer per e-mail zodra uw pakket ons magazijn verlaat.',
    packagingTitle: 'Verpakking & conservering',
    packagingBody: 'Elke zending wordt verpakt in geïsoleerde dozen met koel-gelpacks waar nodig. De buitenverpakking is discreet en zonder merkopdruk. Alle peptiden worden uitsluitend geleverd VOOR ONDERZOEKS­DOELEINDEN, Niet voor menselijke consumptie.',
    trackingTitle: 'Tracking & vervoerders',
    trackingBody: 'Wij werken samen met DHL Express, UPS, FedEx, Royal Mail, Correos en regionale partners. Uw trackingnummer is aanklikbaar in de verzendbevestiging per e-mail en leidt automatisch naar het juiste portaal van de vervoerder.',
    customsTitle: 'Douane & heffingen',
    customsBody: 'Internationale bestellingen kunnen onderhevig zijn aan invoerrechten of belastingen die worden geheven door het land van bestemming. Deze zijn de verantwoordelijkheid van de koper. Als een pakket bij de douane wordt geweigerd, worden de retourverzendkosten ingehouden op eventuele terugbetalingen.',
    contact: 'Vragen over een zending?',
    contactText: 'Ons logistieke team is beschikbaar in het Engels, Spaans en Russisch en antwoordt binnen één werkdag.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (alleen vast tarief)',
  },
    pt: {
    badge: 'Envio e Entrega',
    title: 'Política de Envio',
    lastUpdated: 'Última atualização',
    intro: 'A Penlab Peptide envia peptídeos de pesquisa para todo o mundo a partir de seis centros logísticos. Todos os envios são rastreados, segurados e despachados em embalagens com controle de temperatura para preservar a integridade dos peptídeos.',
    flatRateTitle: 'Envio com taxa fixa',
    flatRateBody: (rate, threshold) => `Uma única taxa fixa de ${rate} (ou equivalente na moeda selecionada) se aplica globalmente em todos os pedidos abaixo de ${threshold}. O envio é gratuito em todos os pedidos iguais ou superiores a ${threshold}. Sem sobretaxas ocultas, sem diferenças por país, sem surpresas no checkout.`,
    freeShippingTitle: 'Envio gratuito em pedidos acima de',
    freeShippingBody: (p) => `Todos os pedidos com subtotal de ${p} ou equivalente na moeda selecionada têm envio gratuito. O desconto é aplicado automaticamente no checkout, nenhum código é necessário.`,
    ratesTitle: 'Taxas de envio por destino',
    zoneCountriesLabel: 'Países',
    zoneRateLabel: 'Taxa fixa',
    zoneFreeLabel: 'Grátis acima de',
    handlingTitle: 'Manuseio e prazo de trânsito',
    handlingBody: 'O prazo de manuseio é de 1 a 2 dias úteis a partir da confirmação do pedido. O prazo de trânsito é normalmente de 3 a 7 dias úteis, dependendo da zona de destino. Você receberá um número de rastreamento por e-mail assim que sua encomenda sair do nosso armazém.',
    packagingTitle: 'Embalagem e preservação',
    packagingBody: 'Cada envio é embalado em caixas isoladas com bolsas de gel refrigerado quando necessário. A embalagem externa é discreta e sem marca. Todos os peptídeos são fornecidos EXCLUSIVAMENTE PARA USO EM PESQUISA, Não destinados ao consumo humano.',
    trackingTitle: 'Rastreamento e transportadoras',
    trackingBody: 'Trabalhamos com DHL Express, UPS, FedEx, Royal Mail, Correos e parceiros regionais. Seu número de rastreamento é clicável no e-mail de confirmação de envio e direciona automaticamente para o portal correto da transportadora.',
    customsTitle: 'Alfândega e impostos',
    customsBody: 'Pedidos internacionais podem estar sujeitos a impostos de importação ou taxas cobradas pelo país de destino. Estes são de responsabilidade do comprador. Se uma encomenda for recusada na alfândega, as taxas de devolução serão deduzidas de qualquer reembolso emitido.',
    contact: 'Dúvidas sobre um envio?',
    contactText: 'Nossa equipe de logística está disponível em inglês, espanhol e russo e responde em até um dia útil.',
    email: 'info@penlabpeptide.com',
    orZoneFallback: ', (apenas taxa fixa)',
  },
  };

const ShippingPolicyPage = () => {
  const { currentLanguage } = useLanguage();
  const { formatPrice } = useCurrency();
  const t = COPY[currentLanguage] || COPY.en;

  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/shipping/policy`);
        if (!cancelled) setPolicy(r.data);
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const threshold = policy?.free_shipping_threshold_eur ?? 100;
  const flatRate = policy?.flat_shipping_rate_eur ?? 25;
  const thresholdFormatted = formatPrice(threshold);
  const flatRateFormatted = formatPrice(flatRate);

  return (
    <div className="min-h-screen bg-capsule-bg" data-testid="shipping-policy-page">
      <SEOHead
        title={`${t.title} | Penlab Peptide`}
        description={t.intro.substring(0, 160)}
        path="/shipping-policy"
      />
      <Header />

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-capsule-teal to-capsule-aqua overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-capsule-teal/90 via-capsule-teal/80 to-transparent" />
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <Truck className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t.badge}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">{t.title}</h1>
            <p className="text-lg text-gray-300">{t.lastUpdated}: April 2026</p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-12 space-y-6">
        {/* Flat-rate callout, single unified EUR rate worldwide */}
        <section
          className="relative overflow-hidden rounded-3xl p-8 bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white shadow-teal-glow"
          data-testid="shipping-flat-rate-hero"
        >
          <div className="absolute right-4 top-4 opacity-20">
            <Package className="w-32 h-32" />
          </div>
          <div className="relative z-10">
            <div className="text-sm uppercase tracking-widest opacity-80 mb-2">{t.flatRateTitle}</div>
            <div className="text-5xl font-extrabold mb-3" data-testid="shipping-flat-rate-amount">{flatRateFormatted}</div>
            <p className="max-w-xl opacity-90 leading-relaxed">
              {t.flatRateBody(flatRateFormatted, thresholdFormatted)}
            </p>
          </div>
        </section>

        {/* Free-shipping hero callout, dynamic currency */}
        <section
          className="relative overflow-hidden rounded-3xl p-8 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-teal-glow"
          data-testid="shipping-free-hero"
        >
          <div className="absolute right-4 top-4 opacity-20">
            <Truck className="w-32 h-32" />
          </div>
          <div className="relative z-10">
            <div className="text-sm uppercase tracking-widest opacity-80 mb-2">{t.freeShippingTitle}</div>
            <div className="text-5xl font-extrabold mb-3" data-testid="shipping-free-threshold">{thresholdFormatted}</div>
            <p className="max-w-xl opacity-90 leading-relaxed">
              {t.freeShippingBody(thresholdFormatted)}
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="glass-card rounded-3xl p-8">
          <p className="text-gray-700 leading-relaxed text-lg">{t.intro}</p>
        </section>

        {/* Rates table */}
        <section className="glass-card rounded-3xl p-8" data-testid="shipping-rates-table">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-xl flex items-center justify-center shadow-lg">
              <Globe2 className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{t.ratesTitle}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Zone</th>
                  <th className="py-2 pr-4">{t.zoneCountriesLabel}</th>
                  <th className="py-2 pr-4">{t.zoneRateLabel}</th>
                  <th className="py-2">{t.zoneFreeLabel}</th>
                </tr>
              </thead>
              <tbody>
                {(policy?.zones || []).map((z) => (
                  <tr key={z.key} className="border-b border-gray-100" data-testid={`zone-row-${z.key}`}>
                    <td className="py-3 pr-4 font-semibold text-gray-900 whitespace-nowrap">{z.name}</td>
                    <td className="py-3 pr-4 text-gray-700">
                      {z.countries && z.countries.length > 0
                        ? z.countries.slice(0, 18).join(', ') + (z.countries.length > 18 ? '...' : '')
                        : 'Rest of World'}
                    </td>
                    <td className="py-3 pr-4 font-mono text-gray-800 whitespace-nowrap">{formatPrice(z.rate_eur)}</td>
                    <td className="py-3 font-mono text-emerald-700 whitespace-nowrap">
                      {z.free_threshold_eur ? formatPrice(z.free_threshold_eur) : t.orZoneFallback}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {[
          { icon: Clock, title: t.handlingTitle, body: t.handlingBody, tone: 'indigo' },
          { icon: Package, title: t.packagingTitle, body: t.packagingBody, tone: 'amber' },
          { icon: Truck, title: t.trackingTitle, body: t.trackingBody, tone: 'teal' },
          { icon: ShieldCheck, title: t.customsTitle, body: t.customsBody, tone: 'rose' },
        ].map(({ icon: Icon, title, body, tone }, i) => (
          <section key={i} className="glass-card rounded-3xl p-8" data-testid={`shipping-section-${i}`}>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                tone === 'indigo' ? 'bg-gradient-to-br from-indigo-500 to-blue-500'
                : tone === 'amber' ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                : tone === 'teal' ? 'bg-gradient-to-br from-capsule-teal to-capsule-aqua'
                : 'bg-gradient-to-br from-rose-500 to-red-500'
              }`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>
                <p className="text-gray-700 leading-relaxed">{body}</p>
              </div>
            </div>
          </section>
        ))}

        <div className="bg-gradient-to-r from-capsule-teal to-capsule-aqua rounded-3xl shadow-teal-glow p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">{t.contact}</h2>
          <p className="mb-6 opacity-90">{t.contactText}</p>
          <a
            href={`mailto:${t.email}`}
            className="inline-flex items-center gap-2 bg-white text-capsule-teal px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            data-testid="shipping-policy-contact-email"
          >
            <Mail className="w-5 h-5" />
            {t.email}
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShippingPolicyPage;
